import React, { useState } from "react";
import { useFormikContext } from "formik";
import { useSelector } from "react-redux";
import { useQueryClient } from "react-query";
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { Buildings } from "@phosphor-icons/react";
import { STATES } from "@constants";
import ItemControlPanelUntyped from "elements/AnItemControlPanel";
import AnItemLinked from "elements/AnItemLinked";
import { ClinicDetailsFields } from "components/Clinics/ClinicDetailsFields";
import { useGetClinics, useCreateClinic } from "hooks/useClinic";
import { GET_CLINICS } from "@constants/reactQueryKeys";
import * as selectors from "selectors";

// ItemControlPanel is a plain-JS component; TS infers overly-narrow prop types
// (e.g. `items: never[]`) from its default parameter values.
const ItemControlPanel = ItemControlPanelUntyped as unknown as (props: any) => JSX.Element;

// Mirrors src/components/Providers/styles/index.js's `searchBar` class so the reused
// AnItemControlPanel search field renders identically to the Provider Library.
const useStyles = makeStyles(() => ({
  searchBar: {
    "& .MuiOutlinedInput-root": {
      color: "#545a72",
      fontWeight: 500,
      "& .MuiInputAdornment-root": { color: "#878da5" },
      "& input": { paddingLeft: "8px" },
    },
  },
}));

interface ProviderClinicSectionProps {
  submitAttempted: boolean;
}

/**
 * Mirrors the "Private Practice" vs "Existing Clinic" branching from the Provider
 * Library's Add Provider drawer (src/components/Providers/index.tsx + ProviderForm.jsx),
 * staged onto the formik `provider` values instead of the library's local dataPanel state.
 */
const ProviderClinicSection: React.FC<ProviderClinicSectionProps> = ({ submitAttempted }) => {
  const classes = useStyles();
  const theme = useTheme();
  const userId = useSelector(selectors.getUserId);
  const queryClient = useQueryClient();
  const { values, errors, setFieldValue } = useFormikContext<any>();

  const provider = values.provider || {};
  const isPrivatePractice = Boolean(provider.privatePractice);
  const physicianClinic: any[] = provider.physicianClinic || [];
  const providerErrors: any = errors.provider || {};

  const [clinicSearch, setClinicSearch] = useState("");
  const [selectedClinicIds, setSelectedClinicIds] = useState<string[]>([]);
  const [newClinicData, setNewClinicData] = useState<any>({});

  const { data: clinicsResponse } = useGetClinics(
    { sort: "clinic_name", direction: "asc", page: 0, size: 1000, clinic_name: clinicSearch },
    { onSuccess: () => {} }
  );
  const { mutateAsync: createClinicAsync, isLoading: isCreatingClinic } = useCreateClinic(
    {}
  ) as unknown as { mutateAsync: (payload: any) => Promise<{ data: any }>; isLoading: boolean };

  const allClinics: any[] = clinicsResponse?.data?.clinics || [];
  const filteredClinics = allClinics
    .filter((clinic) => !physicianClinic.some((item) => item.clinic_id === clinic.id))
    .map((clinic) => ({ id: clinic.id, name: clinic.clinic_name }));

  const handleRemoveClinic = (rowId: string) => {
    setFieldValue(
      "provider.physicianClinic",
      physicianClinic.filter((item) => item.id !== rowId)
    );
  };

  const handleCheckboxSelected = (clinicId: string) => {
    setSelectedClinicIds((prev) =>
      prev.includes(clinicId) ? prev.filter((id) => id !== clinicId) : [...prev, clinicId]
    );
  };

  const handleSubmitClinics = async () => {
    const additions = allClinics
      .filter((clinic) => selectedClinicIds.includes(clinic.id))
      .map((clinic) => ({ id: clinic.id, clinic_id: clinic.id, clinic: { ...clinic } }));
    setFieldValue("provider.physicianClinic", [...physicianClinic, ...additions]);
    setSelectedClinicIds([]);
  };

  const handleNewClinicChange =
    (name: string, onChangeType?: string) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const val =
        onChangeType === "phoneNumberchange" ? value.replace(/[^\d]/g, "").slice(0, 10) : value;
      setNewClinicData((prev: any) => ({ ...prev, [name]: val }));
    };

  const isNewClinicValid = Boolean(
    newClinicData.clinic_name &&
      newClinicData.phone_number &&
      newClinicData.address &&
      newClinicData.city &&
      newClinicData.state &&
      newClinicData.zip_code?.length === 5
  );

  const handleCreateNewClinic = async () => {
    const response = await createClinicAsync({ created_by: userId, ...newClinicData });
    const newClinic = response?.data;
    if (!newClinic?.id) {
      throw new Error("Failed to create clinic: missing id in response");
    }
    setFieldValue("provider.physicianClinic", [
      ...physicianClinic,
      { id: newClinic.id, clinic_id: newClinic.id, clinic: newClinic },
    ]);
    setNewClinicData({});
    queryClient.invalidateQueries([GET_CLINICS]);
  };

  const showClinicError = submitAttempted && providerErrors.physicianClinic;

  return (
    <Box style={{ marginBottom: "24px" }}>
      <Divider sx={{ marginBottom: "24px" }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 500, marginBottom: "4px" }}>
        Clinics
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ marginBottom: "16px" }}>
        Connect the provider to clinics from the clinic library
      </Typography>

      <FormControlLabel
        control={
          <Checkbox
            checked={isPrivatePractice}
            onChange={(e) => setFieldValue("provider.privatePractice", e.target.checked)}
          />
        }
        label="Set as Private Practice"
      />

      {isPrivatePractice ? (
        <Stack gap="24px" sx={{ marginTop: "24px" }}>
          <TextField
            fullWidth
            label="Address"
            value={provider.physicalAddress || ""}
            onChange={(e) => setFieldValue("provider.physicalAddress", e.target.value)}
            error={submitAttempted && !!providerErrors.physicalAddress}
            helperText={submitAttempted && providerErrors.physicalAddress}
            inputProps={{ maxLength: 1000 }}
          />
          <TextField
            fullWidth
            label="Address 2 (optional)"
            value={provider.physicalAddress2 || ""}
            onChange={(e) => setFieldValue("provider.physicalAddress2", e.target.value)}
            inputProps={{ maxLength: 1000 }}
          />
          <TextField
            fullWidth
            label="City"
            value={provider.city || ""}
            onChange={(e) => setFieldValue("provider.city", e.target.value)}
            error={submitAttempted && !!providerErrors.city}
            helperText={submitAttempted && providerErrors.city}
            inputProps={{ maxLength: 255 }}
          />
          <FormControl fullWidth error={submitAttempted && !!providerErrors.state}>
            <InputLabel>State</InputLabel>
            <Select
              label="State"
              value={provider.state || ""}
              onChange={(e) => setFieldValue("provider.state", e.target.value)}
            >
              {Object.entries(STATES).map(([abbr, name]) => (
                <MenuItem key={abbr} value={abbr}>
                  {name as string}
                </MenuItem>
              ))}
            </Select>
            {submitAttempted && providerErrors.state && (
              <FormHelperText>{providerErrors.state}</FormHelperText>
            )}
          </FormControl>
          <TextField
            fullWidth
            label="Zip Code"
            placeholder="23226"
            value={provider.zipCode || ""}
            onChange={(e) => setFieldValue("provider.zipCode", e.target.value.replace(/[^0-9]/g, ""))}
            error={submitAttempted && !!providerErrors.zipCode}
            helperText={submitAttempted && providerErrors.zipCode}
            inputProps={{ maxLength: 5 }}
          />
          <TextField
            fullWidth
            label="Website (optional)"
            placeholder="health.com"
            value={provider.website || ""}
            onChange={(e) => setFieldValue("provider.website", e.target.value)}
          />
        </Stack>
      ) : (
        <Box sx={{ marginTop: "8px" }}>
          <ItemControlPanel
            classes={classes}
            items={physicianClinic.map((item) => ({
              id: item.id,
              clinic_id: item.clinic_id,
              text: item.clinic?.clinic_name,
            }))}
            renderItem={(item: any) => (
              <AnItemLinked
                text={item?.text || ""}
                editMode
                onDeleteItem={() => handleRemoveClinic(item.id)}
                isDeletingItem={false}
              />
            )}
            buttonIcon={
              <Buildings fontSize="20px" weight="duotone" color={theme.palette.primary.main} />
            }
            listItemIcon={<Buildings fontSize="20px" weight="duotone" />}
            buttonText="Connect Clinics"
            titleDialog="Connect Clinic"
            titleSubmitDialog="Connect Clinic"
            descriptionDialog="Choose a clinic below to connect with the Provider's profile."
            onSubmitDialog={handleSubmitClinics}
            onCancelDialog={() => setSelectedClinicIds([])}
            onSearch={setClinicSearch}
            filteredItems={filteredClinics}
            onCheckboxSelected={handleCheckboxSelected}
            isSubmitDisabled={selectedClinicIds.length === 0}
            searchInputPlaceHolder="Search Clinics"
            addNewButtonText="Add New Clinic"
            addNewFormFields={ClinicDetailsFields({
              data: newClinicData,
              errors: {},
              onChange: handleNewClinicChange,
            })}
            onAddNewSubmit={handleCreateNewClinic}
            isAddNewValid={isNewClinicValid}
            isSubmittingAddNew={isCreatingClinic}
          />
          {showClinicError && (
            <FormHelperText error sx={{ marginTop: "8px" }}>
              {providerErrors.physicianClinic}
            </FormHelperText>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ProviderClinicSection;
